import moment from "moment";

import {
  AdminCorporateAction,
  AdminInstrumentTodo,
  AdminInstrumentTodos,
} from "../../../../services/admin";
import { PendingTickerRequest } from "../../../../services/tickerRequests";
import { DateFormat, formatDate } from "../../../../utils";
import { CORPORATE_ACTION_TYPE_LABELS } from "../../../Investments/InvestmentPage/components/ActivityFeed/constants";
import { trimQuantity } from "../../../Investments/utils";
import { formatAgeHours, plural } from "../../utils/format";
import {
  OpenActionItem,
  PendingFixItem,
  PendingRequestItem,
  Resolution,
  ResolvedRequest,
  ResolvedWorkItem,
} from "../types";

export const requestKey = (requestId: number) => `request:${requestId}`;

export const instrumentKey = (instrumentId: number) =>
  `instrument:${instrumentId}`;

const requestBaseOf = (request: PendingTickerRequest) => ({
  key: requestKey(request.id),
  kind: "request" as const,
  symbol: request.symbol,
  subtitle: request.exchangeLabel,
  createdAt: request.createdAt,
  priorityWeight: request.requesterCount,
});

export const toPendingItem = (
  request: PendingTickerRequest,
): PendingRequestItem => {
  const ageHours = moment().diff(moment(request.createdAt), "hours", true);
  return {
    ...requestBaseOf(request),
    status: "pending",
    meta: `${plural(request.requesterCount, "requester")} · ${formatAgeHours(ageHours)}`,
    request,
  };
};

export const decisionLabelOf = (decision: ResolvedRequest["decision"]) =>
  decision === "approved" ? "Approved" : "Rejected";

const fixBaseOf = (instrument: AdminInstrumentTodo) => ({
  key: instrumentKey(instrument.id),
  kind: "instrument" as const,
  symbol: instrument.symbol,
  subtitle: `${instrument.name} · ${instrument.exchange}`,
  createdAt: null,
});

const toPendingFixItem = (
  instrument: AdminInstrumentTodo,
  needsCountry: boolean,
  needsPricing: boolean,
): PendingFixItem => ({
  ...fixBaseOf(instrument),
  status: "pending",
  meta: [
    needsCountry && "No source country",
    needsPricing && "No pricing",
    !instrument.isActive && "Inactive",
  ]
    .filter(Boolean)
    .join(" · "),
  priorityWeight: Number(needsCountry) + Number(needsPricing),
  instrument,
  needsCountry,
  needsPricing,
});

export const toPendingFixItems = ({
  withoutSourceCountry,
  withoutPricing,
}: AdminInstrumentTodos): PendingFixItem[] => {
  const idsOf = (instruments: AdminInstrumentTodo[]) =>
    new Set(instruments.map(({ id }) => id));
  const countryIds = idsOf(withoutSourceCountry);
  const pricingIds = idsOf(withoutPricing);
  const instruments = new Map(
    [...withoutSourceCountry, ...withoutPricing].map((instrument) => [
      instrument.id,
      instrument,
    ]),
  );
  return [...instruments.values()].map((instrument) =>
    toPendingFixItem(
      instrument,
      countryIds.has(instrument.id),
      pricingIds.has(instrument.id),
    ),
  );
};

export const actionKey = (actionId: number) => `corporate:${actionId}`;

export const effectiveDateOf = ({
  effectiveMarketDate,
}: AdminCorporateAction) =>
  formatDate(effectiveMarketDate, DateFormat.DMY_SPACE);

export const typeLabelOf = ({ type }: AdminCorporateAction) =>
  CORPORATE_ACTION_TYPE_LABELS[type];

// Built from the stored strings: a 3-for-7 ratio isn't representable as a float and must stay recognisable.
export const ratioTextOf = ({ newShares, oldShares }: AdminCorporateAction) =>
  newShares === null || oldShares === null
    ? null
    : `${trimQuantity(newShares)}-for-${trimQuantity(oldShares)}`;

const actionBaseOf = (action: AdminCorporateAction) => ({
  key: actionKey(action.id),
  kind: "corporate" as const,
  symbol: action.symbol,
  subtitle: `${action.name} · ${action.exchange}`,
  createdAt: action.createdAt,
});

export const toOpenActionItem = (
  action: AdminCorporateAction,
): OpenActionItem => ({
  ...actionBaseOf(action),
  status: action.deferredAt === null ? "pending" : "deferred",
  meta: [
    typeLabelOf(action),
    `Effective ${effectiveDateOf(action)}`,
    plural(action.preview.openHoldings, "holding"),
  ].join(" · "),
  priorityWeight: action.preview.openHoldings,
  action,
});

export const decisionDetailsOf = (action: AdminCorporateAction) =>
  [
    typeLabelOf(action),
    ratioTextOf(action),
    action.successorSymbol && `to ${action.successorSymbol}`,
    `effective ${effectiveDateOf(action)}`,
  ]
    .filter(Boolean)
    .join(" ");

export const toResolvedItem = (resolution: Resolution): ResolvedWorkItem => {
  if (resolution.kind === "corporate") {
    return {
      ...actionBaseOf(resolution.action),
      status: "resolved",
      meta: `Reconciled · ${typeLabelOf(resolution.action)}`,
      priorityWeight: 0,
      resolution,
    };
  }
  if (resolution.kind === "instrument") {
    return {
      ...fixBaseOf(resolution.instrument),
      status: "resolved",
      meta: "Fix recorded",
      priorityWeight: 0,
      resolution,
    };
  }
  const { request, decision } = resolution;
  return {
    ...requestBaseOf(request),
    status: "resolved",
    meta: `${decisionLabelOf(decision)} · ${plural(request.requesterCount, "requester")}`,
    resolution,
  };
};

// An instrument can lose its country again elsewhere, so a read newer than the fix outranks the resolved entry.
export const isReturnedFix = (
  resolution: Resolution,
  pendingFixes: PendingFixItem[],
  settledAt: number | null,
) =>
  resolution.kind === "instrument" &&
  settledAt !== null &&
  settledAt >= Date.parse(resolution.resolvedAt) &&
  pendingFixes.some(({ key }) => key === resolution.key);
